import { readLocalStorage, getCookie } from "./storage";
import { STORAGE_KEY } from "constants/personalization";
import { AUDIENCE_WEIGHTS } from 'constants/personalization';

const timeSpent = {
	init: ({trigger, score}) => {
		let timerID = setTimeout(() => {
			score(trigger);
			if (timerID)clearTimeout(timerID);
			timerID = null;
		}, parseInt(trigger.value) * 1000);
		
		return () => {
			if (timerID)clearTimeout(timerID);
			timerID = null;
		}
	}
}

const scrollPercentage = {
	init: ({trigger, score}) => {
		// console.log('init scroll percentage');
		const pixel = document.createElement('div');
		pixel.style.position = 'absolute';
		pixel.style.left = 0;
		pixel.style.top = `${parseInt(trigger.value)}%`;
		pixel.style.width = '1px';
		pixel.style.height = '1px';
		document.body.style.position = 'relative';
		pixel.style.visibility = 'hidden';
		document.body.appendChild(pixel);
		const options = {
			threshold: 1,
		}
		const observer = new IntersectionObserver((entries) => {
			const entry = entries.find(item => item.target === pixel && item.isIntersecting === true);
			if (entry) {
				observer.unobserve(pixel);
				document.body.removeChild(pixel);
				score(trigger);
			}
		}, options);
		observer.observe(pixel);

		return () => {
			try {
				observer.unobserve(pixel);
				document.body.removeChild(pixel);
			}
			catch (err) {
				// Catch here in case the target has already been removed.
			}
		}
	}
}

const sessions = {
	init: ({trigger, score}) => {
		// console.log('init sessions');
		const pastSessions = readLocalStorage(STORAGE_KEY)?.sessions;
		if (pastSessions?.length > parseInt(trigger.value)) {
			score(trigger);
		}
	}
}

const returnVisits = {
	init: ({trigger, pathname, score}) => {
		// console.log('init return visits');
		const data = getCookie('z_session');
		if (data?.counter >= parseInt(trigger.value)) {
			score(trigger);
		}
	}
}

const pageActivity = {
	count: 0,
	init: ({trigger, location, score}) => {
		const fallbackSelector = `a[href]`;
		let selector = trigger.value||fallbackSelector;
		let elements;
		try {
			elements = document.querySelectorAll(selector);
		}
		catch (err) {
			elements = document.querySelectorAll(fallbackSelector);
		}
		pageActivity.check = pageActivity.check.bind({trigger, location, score, elements});
		elements.forEach(element => {
			if (typeof element.href !== 'string')return;
			if (selector === fallbackSelector && element.href.indexOf(location.origin) !== 0)return;
			element.addEventListener('focus', pageActivity.check);
			element.addEventListener('click', pageActivity.check);
		});

		return () => {
			elements.forEach(element => {
				element.removeEventListener('focus', pageActivity.check);
				element.removeEventListener('click', pageActivity.check);
			});
		}
	},
	check: function (evt) {
		const { trigger, score, elements } = this;
		const valueRe = /\d+(?:%)?/;
		pageActivity.count++;
		if (trigger.value && valueRe.test(trigger.value)) {
			if (trigger.value.indexOf('%') !== -1) {
				if (pageActivity.count / elements.length < parseInt(trigger.value)/100)return; // We haven't met our goal
			} else {
				if (pageActivity.count < parseInt(trigger.value))return; // We haven't met our goal
			}
		}
		score(trigger);
		elements.forEach(element => {
			element.removeEventListener('focus', pageActivity.check);
			element.removeEventListener('click', pageActivity.check);
		});
	}
}

const formActivity = {
	init: ({trigger, score}) => {
		trigger.value = '#getInTouch';
		const elements = document.querySelectorAll(`${trigger.value||''} input, ${trigger.value||''} textarea`);
		formActivity.check = formActivity.check.bind({trigger, score, elements});
		elements.forEach(element => {
			element.addEventListener('change', formActivity.check);
		});

		return () => {
			elements.forEach(element => {
				element.removeEventListener('change', formActivity.check);
			});
		}
	},
	check: function (evt) {
		const { trigger, score, elements } = this;
		score(trigger);
		elements.forEach(element => {
			element.removeEventListener('change', formActivity.check);
		});
	}
}

const mediaActivity = {
	refs: [],
	init: ({trigger, score}) => {
		if (typeof window === 'undefined')return;
		mediaActivity.progress = mediaActivity.progress.bind({trigger, score});
		mediaActivity.ytListener = mediaActivity.ytListener.bind({trigger});
		const iframes = document.querySelectorAll('iframe');
		iframes.forEach((frame) => {
			if (typeof frame.src !== 'string')return;
			if (frame.src.includes('youtube.com')) {
				const command = JSON.stringify({
					event: 'listening',
				});
				frame.contentWindow.postMessage(command);
				window.addEventListener('message', mediaActivity.ytListener);
			}
			if (frame.src.includes('vimeo.com')) {
				const player = new window.Vimeo.Player(frame);
				player.on('timeupdate', function(data) {
					const percentage = data.percent*100;
					if (percentage >= parseFloat(trigger.value)) {
						player.off('timeupdate');
					}
					mediaActivity.progress({
						...data,
						percentage,
					})
				});
				mediaActivity.refs.push(player);
			}
		});
		return () => {
			window.removeEventListener('message', mediaActivity.ytListener);
			mediaActivity.refs.forEach((player) => {
				player.off('timeupdate');
			});
			mediaActivity.refs = [];
		}
	},
	ytListener: function(evt) {
		const { trigger } = this;
		if (evt.origin !== 'https://www.youtube.com')return;
		const data = JSON.parse(evt.data);
		if (data.event !== 'infoDelivery')return;
		if (data.info?.currentTime === undefined)return;
		if (data.info?.progressState?.duration === undefined)return;
		const percentage = data.info.currentTime / data.info.progressState.duration * 100;
		if (percentage >= parseFloat(trigger.value)) {
			window.removeEventListener('message', mediaActivity.ytListener);
		}
		mediaActivity.progress({
			...data,
			percentage,
		})
	},
	progress: function ({percentage}) {
		const { trigger, score } = this;
		if (percentage >= parseFloat(trigger.value)) {
			score(trigger);
		}
	},
}

const externalLink = {
	count: 0,
	init: ({trigger, location, score}) => {
		const fallbackSelector = `a[href*="://"]`;
		let selector = trigger.value||fallbackSelector;
		let elements;
		try {
			elements = document.querySelectorAll(selector);
		}
		catch (err) {
			elements = document.querySelectorAll(fallbackSelector);
		}
		externalLink.check = externalLink.check.bind({trigger, location, score, elements});
		elements.forEach(element => {
			if (typeof element.href !== 'string')return;
			if (element.href.indexOf(location.origin) === 0)return;
			element.addEventListener('focus', externalLink.check);
			element.addEventListener('click', externalLink.check);
		});

		return () => {
			elements.forEach(element => {
				element.removeEventListener('focus', externalLink.check);
				element.removeEventListener('click', externalLink.check);
			});
		}
	},
	check: function (evt) {
		const { trigger, score, elements } = this;
		const valueRe = /\d+(?:%)?/;
		externalLink.count++;
		if (trigger.value && valueRe.test(trigger.value)) {
			if (trigger.value.indexOf('%') !== -1) {
				if (externalLink.count / elements.length < parseInt(trigger.value)/100)return; // We haven't met our goal
			} else {
				if (externalLink.count < parseInt(trigger.value))return; // We haven't met our goal
			}
		}
		score(trigger);
		elements.forEach(element => {
			element.removeEventListener('focus', externalLink.check);
			element.removeEventListener('click', externalLink.check);
		});
	}
}

const utm = {
	init: ({trigger, score, location}) => {
		const search = new URLSearchParams(location.search);
		if (search.get('utm_campaign') === trigger.value) {
			score(trigger);
		}
	}
}

const impersonate = {
	init: ({score, location, audiences, removeTriggerType}) => {
		const search = new URLSearchParams(location.search);
		if (search.get('impersonate.audienceID')) {
			const trigger = {
				type: 'impersonate',
				id: 0,
				lifespan: 0,
				action: 'set',
				overwrite: true,
			}
			let audienceID = search.get('impersonate.audienceID');
			let weight = (!Number.isNaN(parseInt(search.get('impersonate.weight'))) ? parseInt(search.get('impersonate.weight')) : search.get('impersonate.weight'));
			const audience = audiences.find((item) => {
				if (item.id === audienceID)return true;
				if (item.permalink === audienceID)return true;
				if (item.name === audienceID)return true;
				return false;
			});
			if (!audience) {
				removeTriggerType(trigger.type);
			}
			if (!audience)return;
			if (typeof weight === 'string') {
				try {
					weight = AUDIENCE_WEIGHTS[weight][0] ?? AUDIENCE_WEIGHTS[weight];
				}
				catch (err) {
					weight = AUDIENCE_WEIGHTS[audience.defaultWeight][0] ?? AUDIENCE_WEIGHTS[audience.defaultWeight];
				}
			}
			if (!!weight === false) {
				weight = AUDIENCE_WEIGHTS[audience.defaultWeight][0] ?? AUDIENCE_WEIGHTS[audience.defaultWeight];
			}
			trigger.score = weight;
			trigger.Audience = [
				{
					object: audience,
				}
			];
			score(trigger);
		}
	}
}

const Triggers = {
	timeSpent,
	scrollPercentage,
	sessions,
	returnVisits,
	pageActivity,
	formActivity,
	mediaActivity,
	externalLink,
	utm,
	impersonate,
}

export default Triggers;